'use strict'

const { LANGUAGES, CURRENCIES, NON_BREAKING_SPACE, DEFAULT_SYMBOL_POSITION } = require('./constants')

const get = require('lodash.get')
const isNil = value => value === undefined || value === null

class CurrencyFormatter {
  constructor () {
    this.defaultLanguage = 'en-US'
    this.languages = LANGUAGES
    this.currencies = CURRENCIES
  }

  setDefaultLanguage (language) {
    this.defaultLanguage = language
  }

  format (value, { currency, language, onlyNumbers = false } = {}) {
    const parsedLanguage = this._parseLanguage(language)

    let formatted
    if (this.isZeroDecimal({ currency })) {
      formatted = this._formatNumber(value, 0, language)
    } else {
      formatted = this._formatNumber(value / 100, 2, language)
    }

    let prefix = ''
    let suffix = ''

    if (!onlyNumbers) {
      prefix = this.getPrefix({ currency, language: parsedLanguage })
      suffix = this.getSuffix({ currency, language: parsedLanguage })
    }

    if (value < 0) {
      formatted = formatted.substr(1) // remove minus

      // -150 becomes ($1.50)
      if (parsedLanguage.code === 'en' && parsedLanguage.region.includes('US', null)) {
        prefix = `(${prefix}`
        suffix = `${suffix})`
      } else {
        // In other languages, -150 becomes -$1.50
        prefix = `-${prefix}`
      }
    }

    return `${prefix}${formatted}${suffix}`
  }

  getPrefix ({ currency, language, withSpace = true } = {}) {
    const symbolPosition = this._getLanguageProperty('symbolPosition', { language }) || DEFAULT_SYMBOL_POSITION
    if (symbolPosition === 'before' || (symbolPosition === 'before_with_space' && !withSpace)) {
      return this.getSymbol({ currency })
    } else if (symbolPosition === 'before_with_space') {
      return this.getSymbol({ currency }) + NON_BREAKING_SPACE
    } else {
      return ''
    }
  }

  getSuffix ({ currency, language, withSpace = true } = {}) {
    const symbolPosition = this._getLanguageProperty('symbolPosition', { language }) || DEFAULT_SYMBOL_POSITION
    if (symbolPosition === 'after' || (symbolPosition === 'after_with_space' && !withSpace)) {
      return this.getSymbol({ currency })
    } else if (symbolPosition === 'after_with_space') {
      return NON_BREAKING_SPACE + this.getSymbol({ currency })
    } else {
      return ''
    }
  }

  getSymbol ({ currency }) { 
    return this._getCurrencyProperty('symbol', { currency })
  }

  isZeroDecimal ({ currency } = {}) {
    return this._getCurrencyProperty('zeroDecimal', { currency }) || false
  }

  getThousandSeparator ({ language }) {
    return this._getLanguageProperty('thousandSeparator', { language }) || ''
  }

  getDecimalSeparator ({ language }) {
    return this._getLanguageProperty('decimalSeparator', { language }) || ''
  }

  _getCurrencyProperty (property, { currency } = {}) {
    if (typeof currency !== 'string') {
      throw new Error('Currency must be specified')
    }
    currency = currency.toLowerCase()
    return get(this.currencies, `${currency}.${property}`)
  }

  _getLanguageProperty (property, { language } = {}) {
    const fetch = ({ codeWithRegion, code }) => {
      return get(this.languages, `${codeWithRegion}.${property}`, get(this.languages, `${code}.${property}`))
    }

    let result

    const parsedLanguage = typeof language === 'string' ? this._parseLanguage(language) : language
    if (parsedLanguage) {
      result = fetch(parsedLanguage)
    }

    if (isNil(result)) {
      const defaultLanguage = this._parseLanguage(this.defaultLanguage)
      result = fetch(defaultLanguage)

      if (isNil(result)) {
        result = get(this.languages.en, property)
      }
    }

    return result
  }

  _parseLanguage (language) {
    let [code, region] = (language || this.defaultLanguage).split('-')
    code = code.toLowerCase()
    region = region ? region.toUpperCase() : null
    const codeWithRegion = [code, region].filter(x => x).join('-')
    return { code, region, codeWithRegion }
  }

  _formatNumber (number, decimalPlaces, language) {
    const thousandSeparator = this.getThousandSeparator({ language })
    const decimalSeparator = this.getDecimalSeparator({ language })

    let fixed = number.toFixed(decimalPlaces)
    fixed = fixed.replace('.', decimalSeparator)

    let result = fixed.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator)
    return result
  }
}

module.exports = new CurrencyFormatter()
